import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Edit2, Trash2, Search, ChevronDown, ChevronRight, MapPin, Home, X, Layers, Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminLayout from "@/components/AdminLayout";
import DestinationEditModal from "@/components/DestinationEditModal";

const slugify = (s) => (s || "").toLowerCase()
    .replace(/ı/g, "i").replace(/ş/g, "s").replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ü/g, "u").replace(/ö/g, "o")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/** Walks the tree and yields {node, depth, path} for each destination. */
function flatten(items, depth = 0, path = []) {
    const out = [];
    for (const n of items || []) {
        out.push({ node: n, depth, path: [...path, n.slug] });
        if (n.children && n.children.length) {
            out.push(...flatten(n.children, depth + 1, [...path, n.slug]));
        }
    }
    return out;
}

/** Apply filters (search/region) to tree, keeping ancestors of matches. */
function filterTree(items, search, regionFilter) {
    if (!search && !regionFilter) return items;
    const q = (search || "").toLowerCase();
    const walk = (node) => {
        const children = (node.children || []).map(walk).filter(Boolean);
        const selfMatch =
            (!q || node.name.toLowerCase().includes(q) || (node.slug || "").includes(q)) &&
            (!regionFilter || node.region === regionFilter);
        if (selfMatch || children.length) return { ...node, children };
        return null;
    };
    return items.map(walk).filter(Boolean);
}

const DEPTH_COLORS = ["sky", "indigo", "emerald", "violet", "amber"];

export default function AdminDestinationsPage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();
    const [tree, setTree] = useState([]);
    const [flatList, setFlatList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [regionFilter, setRegionFilter] = useState("");
    const [expanded, setExpanded] = useState({});
    const [editingSlug, setEditingSlug] = useState(null);
    const [createUnder, setCreateUnder] = useState(null); // null = top-level, slug = under that
    const [newDest, setNewDest] = useState({ name: "", slug: "", region: "" });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/destinations?include_children=true");
            setTree(data);
            // Also fetch flat list (no children) for the parent selector
            const { data: flat } = await api.get("/admin/destinations");
            setFlatList(flat);
        } catch (e) {
            toast.error("Yüklenemedi");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (authLoading) return;
        if (!user || !["super_admin", "admin"].includes(user.role)) return navigate("/staff-login");
        load();
    }, [user, authLoading]); // eslint-disable-line

    const regions = useMemo(() => {
        const set = new Set();
        flatList.forEach((d) => d.region && set.add(d.region));
        return Array.from(set).sort();
    }, [flatList]);

    const filtered = useMemo(() => filterTree(tree, search, regionFilter), [tree, search, regionFilter]);

    // Auto-expand when searching
    useEffect(() => {
        if (search || regionFilter) {
            const all = {};
            flatten(filtered).forEach(({ node }) => { all[node.slug] = true; });
            setExpanded(all);
        }
    }, [search, regionFilter, filtered]);

    const totalNodes = flatList.length;
    const totalRoots = tree.length;
    const totalOnHome = flatList.filter((d) => d.show_on_home).length;
    const totalDeep = flatList.filter((d) => (d.level || 1) >= 3).length;

    const toggleActive = async (slug) => {
        try {
            const { data } = await api.post(`/admin/destinations/${slug}/toggle-active`);
            toast.success(data.is_active ? "Aktifleştirildi" : "Pasifleştirildi");
            load();
        } catch (e) {
            toast.error("Güncellenemedi");
        }
    };

    const toggleHome = async (slug) => {
        try {
            const { data } = await api.post(`/admin/destinations/${slug}/toggle-home`);
            toast.success(data.show_on_home ? "Anasayfaya eklendi" : "Anasayfadan kaldırıldı");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Güncellenemedi");
        }
    };

    const deleteDestination = async (slug, name) => {
        if (!window.confirm(`'${name}' destinasyonunu silmek istiyor musunuz?`)) return;
        try {
            await api.delete(`/admin/destinations/${slug}`);
            toast.success("Silindi");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silme başarısız");
        }
    };

    const createDestination = async () => {
        if (!newDest.name) { toast.error("İsim zorunlu"); return; }
        const slug = newDest.slug || slugify(newDest.name);
        try {
            await api.post("/admin/destinations", {
                slug,
                name: newDest.name,
                country: "Türkiye",
                region: newDest.region || (createUnder ? (flatList.find((d) => d.slug === createUnder)?.region || "") : ""),
                parent_slug: createUnder || null,
                is_active: true,
                sort_order: 0,
            });
            toast.success("Eklendi");
            setCreateUnder(null);
            setNewDest({ name: "", slug: "", region: "" });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eklenemedi");
        }
    };

    const renderRow = (node, depth) => {
        const hasChildren = (node.children || []).length > 0;
        const isOpen = !!expanded[node.slug];
        const color = DEPTH_COLORS[Math.min(depth, DEPTH_COLORS.length - 1)];

        return (
            <React.Fragment key={node.slug}>
                <tr className="border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="px-2 py-2.5" style={{ paddingLeft: `${0.5 + depth * 1.5}rem` }}>
                        <div className="flex items-center gap-2">
                            {hasChildren ? (
                                <button
                                    onClick={() => setExpanded((e) => ({ ...e, [node.slug]: !e[node.slug] }))}
                                    className="p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700"
                                    data-testid={`dest-expand-${node.slug}`}
                                >
                                    {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                </button>
                            ) : (
                                <span className="w-5" />
                            )}
                            <MapPin className={`h-4 w-4 flex-shrink-0 text-${color}-500`} />
                            <div className="min-w-0">
                                <div className={`font-semibold text-slate-900 dark:text-white text-sm truncate`}>
                                    {node.name}
                                </div>
                                <div className="text-[10px] font-mono text-slate-400 truncate">{node.slug}</div>
                            </div>
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-${color}-100 dark:bg-${color}-950/40 text-${color}-700 dark:text-${color}-300`}>
                                L{node.level || depth + 1}
                            </span>
                        </div>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-600 dark:text-slate-400">{node.region || "-"}</td>
                    <td className="px-3 py-2.5 text-center text-sm font-semibold text-slate-700 dark:text-slate-300">{node.hotel_count || 0}</td>
                    <td className="px-3 py-2.5 text-center">
                        {hasChildren ? (
                            <span className="inline-flex px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-semibold">{node.children.length}</span>
                        ) : (
                            <span className="text-slate-400 text-xs">—</span>
                        )}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                        <button onClick={() => toggleActive(node.slug)} data-testid={`dest-toggle-${node.slug}`}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${node.is_active === false ? "bg-slate-300 dark:bg-slate-600" : "bg-emerald-500"}`}>
                            <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${node.is_active === false ? "translate-x-1" : "translate-x-5"}`} />
                        </button>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                        <button
                            onClick={() => toggleHome(node.slug)}
                            data-testid={`dest-home-${node.slug}`}
                            title={node.show_on_home ? "Anasayfadan kaldır" : "Anasayfada göster"}
                            className={`inline-flex h-7 w-7 items-center justify-center rounded-lg transition-colors ${node.show_on_home ? "bg-amber-100 dark:bg-amber-950/40 text-amber-600" : "bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-slate-600"}`}
                        >
                            <Home className="h-3.5 w-3.5" />
                        </button>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                        <div className="inline-flex items-center gap-1">
                            <button
                                onClick={() => { setCreateUnder(node.slug); setNewDest({ name: "", slug: "", region: node.region || "" }); }}
                                className="px-2 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 hover:bg-emerald-100 dark:hover:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 text-[11px] font-bold inline-flex items-center gap-1"
                                title="Alt destinasyon ekle"
                                data-testid={`dest-add-sub-${node.slug}`}
                            >
                                <Plus className="h-3 w-3" /> Alt
                            </button>
                            <button onClick={() => setEditingSlug(node.slug)} className="p-1.5 rounded-lg hover:bg-sky-50 dark:hover:bg-sky-950/30 text-sky-600" title="Düzenle" data-testid={`dest-edit-${node.slug}`}>
                                <Edit2 className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => deleteDestination(node.slug, node.name)} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600" title="Sil" data-testid={`dest-delete-${node.slug}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    </td>
                </tr>
                {/* Inline create-sub row */}
                {createUnder === node.slug && (
                    <tr className="bg-emerald-50/50 dark:bg-emerald-950/20" data-testid={`dest-inline-create-${node.slug}`}>
                        <td colSpan="7" className="p-3" style={{ paddingLeft: `${1 + (depth + 1) * 1.5}rem` }}>
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[11px] font-bold uppercase tracking-widest text-emerald-700 dark:text-emerald-300">↳ {node.name} altına ekle:</span>
                                <input
                                    value={newDest.name}
                                    onChange={(e) => setNewDest({ ...newDest, name: e.target.value, slug: slugify(e.target.value) })}
                                    placeholder="Alt destinasyon adı (örn: Mahmutlar)"
                                    className="flex-1 min-w-[200px] px-3 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-sm"
                                    data-testid="new-sub-name"
                                    autoFocus
                                />
                                <input
                                    value={newDest.slug}
                                    onChange={(e) => setNewDest({ ...newDest, slug: e.target.value })}
                                    placeholder="slug"
                                    className="w-32 px-3 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-xs font-mono"
                                    data-testid="new-sub-slug"
                                />
                                <button
                                    onClick={createDestination}
                                    className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold"
                                    data-testid="new-sub-save"
                                >Kaydet</button>
                                <button
                                    onClick={() => { setCreateUnder(null); setNewDest({ name: "", slug: "", region: "" }); }}
                                    className="p-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500"
                                ><X className="h-4 w-4" /></button>
                            </div>
                        </td>
                    </tr>
                )}
                {/* Children */}
                {isOpen && (node.children || []).map((c) => renderRow(c, depth + 1))}
            </React.Fragment>
        );
    };

    return (
        <AdminLayout title="Destinasyonlar">
            <div className="space-y-4" data-testid="admin-destinations-page">
                {/* Header */}
                <div className="flex flex-wrap items-center gap-3 justify-between">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Hiyerarşik Yönetim</div>
                        <h2 className="font-display text-xl font-bold text-slate-900 dark:text-white">
                            {totalNodes} destinasyon · <span className="text-sky-600">{totalRoots} kök</span> · <span className="text-emerald-600">{totalDeep} derin (L3+)</span> · <span className="text-amber-600">{totalOnHome} anasayfada</span>
                        </h2>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Adı veya slug ile ara..."
                                className="pl-9 pr-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm w-64"
                                data-testid="dest-search"
                            />
                        </div>
                        <select value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)} className="px-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm" data-testid="dest-region-filter">
                            <option value="">Tüm bölgeler</option>
                            {regions.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                        <button onClick={() => { setCreateUnder("__root__"); setNewDest({ name: "", slug: "", region: "" }); }} className="px-4 py-2 rounded-xl bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-600 hover:to-indigo-700 text-white text-sm font-semibold flex items-center gap-1.5 shadow-lg shadow-sky-500/20" data-testid="dest-create-btn">
                            <Plus className="h-4 w-4" /> Kök Destinasyon
                        </button>
                        <button
                            onClick={() => {
                                const all = {};
                                flatList.forEach((d) => { all[d.slug] = true; });
                                setExpanded(all);
                            }}
                            className="px-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm font-semibold flex items-center gap-1.5 hover:bg-slate-50 dark:hover:bg-slate-800"
                            data-testid="dest-expand-all"
                        ><Layers className="h-4 w-4" /> Tümünü Aç</button>
                        <button
                            onClick={() => setExpanded({})}
                            className="px-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800"
                        >Kapat</button>
                        <button
                            onClick={async () => {
                                if (!window.confirm("Tüm Türkiye destinasyonları (200+) yüklenecek. Mevcut kayıtlar korunur, eksikler eklenir. Devam edilsin mi?")) return;
                                try {
                                    const r = await api.post("/admin/destinations/seed-defaults");
                                    alert(`✓ Başarılı!\n\nEklendi/güncellendi: ${r.data.destinations_seeded} destinasyon\nToplam: ${r.data.total_destinations} destinasyon`);
                                    load();
                                } catch (e) {
                                    alert("✗ Hata: " + (e.response?.data?.detail || e.message));
                                }
                            }}
                            className="px-4 py-2 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-600 hover:from-violet-600 hover:to-fuchsia-700 text-white text-sm font-bold flex items-center gap-1.5 shadow-lg shadow-violet-500/20"
                            data-testid="dest-seed-defaults"
                            title="Tüm Türkiye şehirlerini ve popüler tatil bölgelerini tek tıkla yükle"
                        >
                            <Download className="h-4 w-4" /> Türkiye Şehirlerini Yükle
                        </button>
                    </div>
                </div>

                {/* Top-level create */}
                {createUnder === "__root__" && (
                    <div className="p-4 rounded-2xl border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/30" data-testid="dest-create-form">
                        <h3 className="font-display text-sm font-bold mb-3 text-slate-900 dark:text-white">Yeni Kök Destinasyon (üst seviye)</h3>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <input
                                value={newDest.name}
                                onChange={(e) => setNewDest({ ...newDest, name: e.target.value, slug: slugify(e.target.value) })}
                                placeholder="İsim (örn: Bursa)"
                                className="px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-sm"
                                data-testid="new-root-name"
                            />
                            <input
                                value={newDest.slug}
                                onChange={(e) => setNewDest({ ...newDest, slug: e.target.value })}
                                placeholder="slug (URL)"
                                className="px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-sm font-mono"
                            />
                            <input
                                value={newDest.region}
                                onChange={(e) => setNewDest({ ...newDest, region: e.target.value })}
                                placeholder="Bölge (örn: Marmara)"
                                className="px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-sm"
                            />
                            <div className="flex gap-2">
                                <button onClick={createDestination} className="flex-1 px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold" data-testid="new-root-save">Kaydet</button>
                                <button onClick={() => { setCreateUnder(null); setNewDest({ name: "", slug: "", region: "" }); }} className="px-3 py-2 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600"><X className="h-4 w-4" /></button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tree */}
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
                    {loading ? (
                        <div className="p-10 text-center text-slate-500">Yükleniyor...</div>
                    ) : filtered.length === 0 ? (
                        <div className="p-10 text-center text-slate-500">
                            {search || regionFilter ? "Sonuç yok" : "Destinasyon yok"}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 dark:bg-slate-800/50 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                                    <tr>
                                        <th className="text-left px-4 py-3">Destinasyon</th>
                                        <th className="text-left px-3 py-3">Bölge</th>
                                        <th className="text-center px-3 py-3">Otel</th>
                                        <th className="text-center px-3 py-3">Alt</th>
                                        <th className="text-center px-3 py-3">Aktif</th>
                                        <th className="text-center px-3 py-3">Anasayfa</th>
                                        <th className="text-right px-3 py-3">İşlem</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((n) => renderRow(n, 0))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {editingSlug && (
                <DestinationEditModal slug={editingSlug} onClose={() => setEditingSlug(null)} onChanged={load} />
            )}
        </AdminLayout>
    );
}
